<template>
  <div>
    <div class="p-4 flex flex-col gap-2 rounded-2xl bg-slate-200">
      <div class="flex items-center justify-between">
        <BlTextSubtitle2 :text="sectionName" class="text-slate-800" />
        <BlInputButton
          icon="edit"
          type="plain"
          @clicked="editOpened = true"
          title="Edit section"
        />
      </div>
      <div class="flex flex-col gap-1">
        <BlTextBody2 :text="referralInput.label" />
        <BlTextBody2 :text="prospectReferral" class="text-slate-1000" />
      </div>
      <div v-if="enableBranch" class="flex flex-col gap-1">
        <BlTextBody2 :text="branchInput.label" />
        <BlTextBody2
          :text="
            (prospectInformation.branch && prospectInformation.branch.name) ||
              '-'
          "
          class="text-slate-1000"
        />
      </div>
      <div class="flex flex-col gap-1">
        <BlTextBody2 :text="prospectTagsInput.label" />
        <BlTextBody2
          v-if="
            !prospectInformation.prospect_tags ||
              !prospectInformation.prospect_tags.length
          "
          :text="'-'"
          class="text-slate-1000"
        />
        <div v-else>
          <BlTextBody2
            :text="
              prospectInformation.prospect_tags.map(i => i.name).join(', ')
            "
            class="text-slate-1000"
          />
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <BlTextBody2 :text="assignedToInput.label" />
        <BlTextBody2
          v-if="!prospectInformation.assigned_user"
          text="-"
          class="text-slate-1000"
        />
        <BlTextBody2
          v-else
          :text="prospectInformation.assigned_user.name || 'Deleted user'"
          class="text-slate-1000"
          :class="{ 'italic ': !prospectInformation.assigned_user.name }"
        />
      </div>
    </div>
    <div
      id="edit-client-information-panel"
      class="fixed w-screen right-0 flex justify-end transition-all duration-500"
      :style="{
        top: editOpened ? 'calc(4.5rem + 5rem)' : 'calc(100vh)',
        height: 'calc(100vh - 4.5rem - 5rem)',
        zIndex: 53
      }"
    >
      <div
        class="h-full w-screen absolute left-0 top-0 -z-10"
        @click="editOpened = false"
      ></div>
      <div class="h-full w-120 flex-shrink-0 flex flex-col relative z-0">
        <div
          class="relative h-full flex flex-col justify-between bg-pure-white overflow-y-auto"
        >
          <div class="px-6 flex flex-col">
            <div class="sticky top-0 z-10 py-3 bg-pure-white mb-0">
              <BlTextSubtitle2
                :text="`Edit ${sectionName}`"
                class="text-slate-800"
              />
            </div>
            <div class="flex flex-col gap-3">
              <BlSelectBox
                :label="referralInput.label"
                v-model="referralInput.model"
                placeholder="Select referral"
                :options="referralOptions"
                @change="referralInput.model = $event"
              />
              <BlSelectBox
                v-if="enableBranch"
                type="branch"
                :label="branchInput.label"
                placeholder="Select branch"
                v-model="branchInput.model"
                :options="branchOptions"
                search
                @change="branchInput.model = $event"
              />
              <BlSearchBox
                :label="prospectTagsInput.label"
                v-model="prospectTagsInput.model"
                placeholder="Select tags"
                :options="
                  prospectTags.map(i => ({ value: i.id, label: i.name }))
                "
                @change="prospectTagsInput.model = $event"
              />
              <BlSelectBox
                :label="assignedToInput.label"
                v-model="assignedToInput.model"
                placeholder="Select therapist"
                :options="assignedToOptions"
                @change="assignedToInput.model = $event"
              />
            </div>
          </div>
          <div
            class="h-18 px-6 pt-4 sticky bottom-0 flex-shrink-0 bg-pure-white grid grid-cols-2 gap-4"
          >
            <BlInputButton
              label="Close"
              type="plain"
              @clicked="editOpened = false"
            />
            <BlInputButton
              label="Update"
              :loading="submitLoading"
              @clicked="onUpdate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import BlTextSubtitle2 from "../../../../BlTextSubtitle2.vue";
import BlTextBody2 from "../../../../BlTextBody2.vue";
import BlSelectBox from "../../../../BlSelectBox.vue";
import BlSearchBox from "../../../../BlSearchBox.vue";
import BlInputButton from "../../../../BlInputButton.vue";

export default {
  name: "AdditionalInformationSection",
  components: {
    BlTextSubtitle2,
    BlTextBody2,
    BlSelectBox,
    BlSearchBox,
    BlInputButton
  },
  props: {
    // @after-commit
  },
  data() {
    return {
      sectionName: "Additional information",
      editOpened: false,
      submitLoading: false,
      referralInput: { label: "Referral", model: "" },
      referralOptions: [
        { value: "friend_or_family", label: "Friend/family" },
        { value: "outside_provider", label: "Outside provider" },
        { value: "school_or_daycare", label: "School/daycare" },
        { value: "doctor", label: "Doctor" },
        { value: "walk_in", label: "Walk-in" },
        { value: "website", label: "Website" },
        { value: "social_media", label: "Social media" },
        { value: "community_event", label: "Community event" },
        { value: "other", label: "Other" }
      ],
      branchInput: { label: "Branch tag", model: "" },
      prospectTagsInput: { label: "Prospect's tag", model: [] },
      assignedToInput: { label: "Assigned to", model: "" },
      assignedToOptions: []
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "prospectInformation",
      "branches",
      "prospectTags"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    branchOptions() {
      const noBranch = {
        value: null,
        label: "No branch",
        color: "#667085",
        backgroundColor: "#F9FAFB"
      };
      const arr = this.branches.map(i => ({
        value: i.id,
        label: i.name,
        color: i.color,
        backgroundColor: i.background_color
      }));
      return [noBranch, ...arr];
    },
    additional() {
      return this.prospectInformation.additional_information || {};
    },
    prospectReferral() {
      const found = this.referralOptions.find(
        i => i.value === this.prospectInformation.referral
      );
      if (!found) return "-";
      return found.label;
    }
  },
  watch: {
    editOpened(val) {
      // prettier-ignore
      if (val) {
        this.referralInput.model = this.prospectInformation.referral;
        this.branchInput.model = this.prospectInformation.branch_id;
        const tags = this.prospectInformation.prospect_tags || [];
        this.prospectTagsInput.model = tags.map(i => i.id);
        this.assignedToInput.model = this.prospectInformation.assigned_user_id;
      }
    }
  },
  created() {
    this.getProspectTags({ params: "?sort=name_asc" }); // for get all tags (options)
    this.getUsers({
      search: true,
      params: "?sort=name_asc&status=active&role=admin"
    }).then(({ success, data }) => {
      if (!success) return;
      this.assignedToOptions = data.map(i => ({ value: i.id, label: i.name }));
    });
  },
  methods: {
    ...mapActions(["getUsers", "getProspectTags", "updateProspect"]),
    displayDate(date, format) {
      if (!date) return "-";
      return moment(date).format(format);
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    onUpdate() {
      // prettier-ignore
      const data = {
        id: this.prospectInformation.id,
        prospect: {
          referral: this.referralInput.model,
          branch_id: this.branchInput.model,
          prospect_tag_ids: this.prospectTagsInput.model,
          assigned_user_id: this.assignedToInput.model,
          section_name: this.sectionName
        }
      };
      this.submitLoading = true;
      this.updateProspect(data).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.editOpened = false;
        this.$emit("after-commit");
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/modal/prospectPanel/clientInformation/AdditionalInformationSection.vue